import type { IContentfulFilters } from 'api/contentful/fetchEntries';
import {
  ContentfulEntryFilter,
  ContentfulFilterRenderProps,
} from 'components/EntryLoader/ContentfulEntryFilter';
import type { Entry } from 'contentful';
import * as React from 'react';
import type { WithTranslation } from 'react-i18next';

/**
 * Enforces the render/children function type
 */
export type RenderPropFunc<T extends object = any> = (
  props: ContentfulEntryRenderProps<T>,
) => JSX.Element | null | Array<JSX.Element | null>;

/**
 * The props that need to be provided to ContentfulEntry
 */
type ContentfulEntryProvidedProps<EntryType extends object> = IContentfulFilters<EntryType> & {
  entryId?: string;

  // Allow defining the render props by either a prop or as children
  render?: RenderPropFunc<EntryType>;
  children?: RenderPropFunc<EntryType>;
};

/**
 * The props that are passed into the nested render function
 */
export type ContentfulEntryRenderProps<EntryType extends object = any> = WithTranslation & {
  entry?: Entry<EntryType>;
  isLoading: boolean;
  notFound: boolean;
  hasError: boolean;
};

/**
 * Render prop component that fetches a single entry from Contentful.
 * @See https://reactjs.org/docs/render-props.html
 *
 * Example Usage:
 *  <ContentfulEntry entryId={id}>
 *    {(props: RenderProps) => (
 *      <MyAssembly {...props}>
 *    )}
 *  </ContentfulEntry>
 */
function ContentfulEntry<EntryType extends object = any>(props: ContentfulEntryProvidedProps<EntryType>) {
  const { filters, children, render, ...remainingProps } = props;

  let newFilters = filters;
  if (props.entryId) {
    newFilters = { ...(props.filters || {}), sys: { id: props.entryId } };
  }

  if (!render && !children) {
    console.error('No render props were provided to ContentfulEntry.');
    return null;
  } else if (!!render && !!children) {
    console.error(
      'You may provide either a render function as a prop OR as a child to ContentfulEntry but not both.',
    );
    return null;
  }

  const renderFunc: RenderPropFunc<EntryType> = children || render!;

  return (
    <ContentfulEntryFilter
      {...remainingProps}
      filters={newFilters}
      render={({ entries, ...remaining }: ContentfulFilterRenderProps<EntryType>) => {
        if (!entries || !entries.items.length) {
          // Omit the entry if it is not found
          return renderFunc(remaining);
        }

        // Validate that we only have one entry returned
        if (entries.items.length !== 1) {
          console.warn(`Expected exactly one entry returned but got ${entries.items.length}.`);
        }

        return renderFunc({ ...remaining, entry: entries.items[0] });
      }}
    />
  );
}

export { ContentfulEntry };
